<template>
  <div id="raised-card">
    <div class="exchange-container">
      <SmallLoader v-if="loading" />
      <template v-else>
        <div v-if="shownTokens.length > 0" class="exchange-list">
          <router-link
            v-for="(token, index) in shownTokens"
            :key="index"
            :to="{
              name: 'TokenOverview',
              params: { symbol: token.symbol, id: token._id },
            }"
            class="exchange-row"
            :id="token.symbol"
          >
            <!-- Top Row: Token Info -->
            <div class="row-top">
              <div class="token-logo">
                <img 
                  :src="token.image" 
                  :alt="token.name" 
                  loading="lazy"
                  @error="handleImageError"
                />
              </div>
              <div class="token-info">
                <h3 class="token-name">{{ token.name }}</h3>
                <div class="token-meta">
                  <span class="token-symbol">{{ token.symbol }}</span>
                  <DealTypeBadge :badge="viewOf(token).badge" size="sm" />
                  <ShariaBadge :token="token" size="sm" />
                </div>
              </div>
              <span class="trade-btn">
                Trade
                <svg class="tw-w-4 tw-h-4" viewBox="0 0 16 16" fill="none">
                  <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>

            <!-- Bottom Row: Price & Stats -->
            <div class="row-bottom">
              <div class="price-block">
                <span class="price-value">{{ formatAmountToDollar(token.price?.exchange || 0, priceDecimals(token), currency(token)) }}</span>
                <span v-if="priceUnit(token)" class="price-unit">{{ priceUnit(token) }}</span>
                <div class="price-change" :class="getChangeClass(token)">
                  <svg v-if="parseFloat(token.percentage) >= 0" class="change-icon" viewBox="0 0 12 12" fill="none">
                    <path d="M6 9V3M6 3L3 6M6 3L9 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else class="change-icon" viewBox="0 0 12 12" fill="none">
                    <path d="M6 3V9M6 9L3 6M6 9L9 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>{{ formatPercentage(token.percentage) }}%</span>
                </div>
              </div>
              <div class="stats-row">
                <div v-for="pill in viewOf(token).pills" :key="pill.k" class="stat-pill">
                  <span class="stat-label">{{ pill.k }}</span>
                  <span class="stat-value" :class="{ highlight: pill.tone === 'up', down: pill.tone === 'down' }">{{ pill.v }}</span>
                </div>
              </div>
            </div>
          </router-link>
        </div>
        <div class="empty-container" v-else>
          <EmptyState>
            <template v-slot:message>
              <p class="empty-text" v-if="hasSearchQuery">
                No company with that name exists
              </p>
              <p class="empty-text" v-else-if="typeFilter">
                No deals of that kind are trading right now
              </p>
              <p class="empty-text" v-else>
                No companies listed on the exchange yet
              </p>
            </template>
          </EmptyState>
        </div>
      </template>
      <div v-if="$route.name !== 'OverviewDetails' && !typeFilter && pages > 0" class="pagination-container">
        <Pagination
          :totalPage="pages"
          :currentPage="page"
          :maxVisible="5"
          @pageChange="gotoPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/navigation/Pagination";
import EmptyState from "@/components/notification/EmptyState";
import { formatAmountToDollar } from "@/utils/helpers.js";
import DealTypeBadge from "@/components/general/DealTypeBadge.vue";
import ShariaBadge from "@/components/general/ShariaBadge.vue";
import { DealKinds, listingView, priceDecimals } from "@/utils/trade-view";

/** How many listings the filter loads to narrow: all of them, at today's volumes. */
const FILTER_LIMIT = 100;
import { mapActions, mapState } from "vuex";

export default {
    name: "RaisedCard",

    components: { Pagination, EmptyState, DealTypeBadge, ShariaBadge },
    data() {
      return {
        loading: false,
      };
    },

    computed: {
      ...mapState({
        companiesRaised: (state) => state.TokenModule.raisedTokens,
        page: (state) => state.TokenModule.raisedPage,
        pages: (state) => state.TokenModule.raisedPages,
      }),
      views() {
        return new Map((this.companiesRaised || []).map((token) => [token._id, listingView(token)]));
      },
      // Narrows the current page, like the offering filter on Now Raising.
      shownTokens() {
        const list = this.companiesRaised || [];
        return this.typeFilter ? list.filter((token) => this.viewOf(token).badge.tone === this.typeFilter) : list;
      },
    },
    props: {
      // A badge tone from listingView (debt, equity, safe, fund, commodity, ngx); empty for all.
      typeFilter: {
        type: String,
        required: false,
        default: "",
      },
      hasSearchQuery: {
        type: Boolean,
        required: false,
        default: () => {
          return false;
        },
      },
    },

    watch: {
      // The filter runs on what is loaded, so while it is on the whole list is loaded and the
      // page numbers, which count the unfiltered list, are hidden.
      typeFilter(on) {
        if (this.hasSearchQuery) return;
        if (on) this.loadAll();
        else this.getDetails();
      },
    },

    mounted() {
      if (!this.hasSearchQuery) {
        if (this.typeFilter) this.loadAll();
        else this.getDetails();
      }
    },

    methods: {
      ...mapActions("TokenModule", ["getCompaniesRaised"]),

      formatAmountToDollar,

      currency(token) {
        return token.currency || 'USD';
      },

      viewOf(token) {
        return this.views.get(token._id) || listingView(token);
      },

      priceDecimals,

      // What the price is for, where it is not obviously a share: a gram of a metal, a fund unit.
      priceUnit(token) {
        const view = this.viewOf(token);
        if (view.kind === DealKinds.COMMODITY) return view.hero?.unit || "per token";
        if (view.kind === DealKinds.FUND) return "per unit";
        if (view.kind === DealKinds.LISTED) return "per share";
        return "";
      },

      getChangeClass(token) {
        const change = parseFloat(token.percentage) || 0;
        return change >= 0 ? 'positive' : 'negative';
      },

      formatPercentage(value) {
        const num = parseFloat(value) || 0;
        return num >= 0 ? `+${num.toFixed(2)}` : num.toFixed(2);
      },

      handleImageError(e) {
        e.target.src = require("@/assets/img/token-icon.svg");
      },

      // Everything trading, for the filter to narrow. Listings number well under this.
      async loadAll() {
        this.loading = true;
        await this.getCompaniesRaised({ page: 1, limit: FILTER_LIMIT });
        this.loading = false;
      },

      async getDetails() {
        this.loading = true;
        let limit = 6;
        if (this.$route.name !== "OverviewDetails") limit = 12;
        const payload = { page: 1, limit: limit };
        await this.getCompaniesRaised(payload);
        this.loading = false;
      },

      async gotoPage(page) {
        this.loading = true;
        let limit;
        if (this.$route.name !== "OverviewDetails") limit = 12;
        const payload = {
          page: page,
          limit: limit,
        };
        await this.getCompaniesRaised(payload);
        this.loading = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
#raised-card {
  .exchange-container {
    width: 100%;
  }

  .exchange-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .exchange-row {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 11px;
    padding: 16px 20px;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      border-color: #4D84FF;
      background: #FFFFFF;

      .trade-btn {
        color: #4D84FF;
      }
    }
  }

  // Top Row
  .row-top {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .token-logo {
    width: 44px;
    height: 44px;
    border-radius: 4px;
    overflow: hidden;
    background: #F8FAFC;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .token-info {
    flex: 1;
    min-width: 0;
  }

  .token-name {
    font-size: 16px;
    font-weight: 400;
    color: #0F172A;
    margin: 0 0 4px 0;
    line-height: 1.3;
  }

  .token-meta {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .token-symbol {
    font-size: 13px;
    color: #64748B;
    font-weight: 400;
  }


  .trade-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 400;
    color: #64748B;
    transition: color 0.2s ease;
    flex-shrink: 0;
  }

  // Bottom Row
  .row-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-top: 12px;
    border-top: 1px solid #F1F5F9;
  }

  .price-block {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .price-value {
    font-size: 18px;
    font-weight: 400;
    color: #0F172A;
  }

  .price-unit {
    font-size: 12px;
    color: #64748B;
  }

  .price-change {
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 4px 8px;
    border-radius: 11px;
    font-size: 12px;
    font-weight: 400;

    &.positive {
      background: #ECFDF5;
      color: #10B981;
    }

    &.negative {
      background: #FEF2F2;
      color: #EF4444;
    }
  }

  .change-icon {
    width: 10px;
    height: 10px;
  }

  .stats-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .stat-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #F8FAFC;
    border-radius: 4px;
  }

  .stat-label {
    font-size: 11px;
    color: #94A3B8;
    font-weight: 400;
  }

  .stat-value {
    font-size: 13px;
    font-weight: 400;
    color: #0F172A;

    &.highlight {
      color: #10B981;
    }

    &.down {
      color: #EF4444;
    }
  }

  .empty-container {
    padding: 40px 20px;
    text-align: center;
  }

  .empty-text {
    color: #64748B;
    font-size: 14px;
  }

  .pagination-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
  }

  // Responsive
  @media (max-width: 640px) {
    .row-bottom {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    .stats-row {
      flex-wrap: wrap;
    }

    .stat-pill {
      padding: 4px 8px;
    }

    .price-value {
      font-size: 18px;
    }
  }
}
</style>
